import * as React from 'react';
import { useEventCallback, useForkRef, useIsomorphicEffect } from '../../hooks';
import { FixMeLater } from '../../utils';
import { useFieldBase } from '../FieldBase/FieldBase.context';

export interface SelectBaseProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size' | 'width'> {
    /**
     * The option elements that the `select` element will be populated with.
     */
    children: React.ReactElement<HTMLOptionElement>[];

    /**
     * The default value. Use when the component is not controlled.
     */
    defaultValue?: string;

    /**
     * If `true`, the component will be disabled.
     */
    disabled?: boolean;

    /**
     * If `true`, the `select` element will have the `aria-invalid` attribute.
     */
    error?: boolean;

    /**
     * Callback fired when the `select` element loses focus.
     * @param {React.FocusEvent} event The event source of the callback.
     */
    onBlur?: (event: React.FocusEvent<HTMLSelectElement>) => void;

    /**
     * Callback fired when the value of the `select` element is changed.
     * @param {React.ChangeEvent} event The event source of the callback.
     */
    onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;

    /**
     * Callback fired when the `select` element loses focus.
     * @param {React.FocusEvent} event The event source of the callback.
     */
    onFocus?: (event: React.FocusEvent<HTMLSelectElement>) => void;

    /**
     * The `tabindex` attribute of the `select` element.
     * @default 0
     */
    tabIndex?: number;

    /**
     * The value of the `select` element. Required if the component is controlled.
     */
    value?: string;

    /**
     * Allow any data-* attribute.
     */
    [key: `data-${string}`]: string;
}

export const SelectBase = React.forwardRef(function SelectBase(
    props: SelectBaseProps,
    ref: React.ForwardedRef<FixMeLater>
): React.JSX.Element {
    const { children, disabled, error, onBlur, onChange, onFocus, tabIndex = 0, value: valueProp, ...other } = props;

    const { setFilled, setFocused, controlRef } = useFieldBase();
    const { current: isControlled } = React.useRef(valueProp !== undefined);

    const elementRef = React.useRef<HTMLSelectElement | null>(null);
    const handleRefIntermediary = useForkRef(
        elementRef,
        controlRef as ((instance: HTMLSelectElement | null) => void) | null
    );
    const handleRef = useForkRef(handleRefIntermediary, ref);

    React.useEffect(() => {
        // Handle cases where the autoFocus prop is used, as this will not neccessarliy trigger onFocus to fire.
        if (document && document.activeElement === elementRef.current) {
            if (setFocused) {
                setFocused(true);
            }
        }
    }, [setFocused]);

    const handleBlur = useEventCallback((event: React.FocusEvent<HTMLSelectElement>): void => {
        onBlur?.(event);
        setFocused?.(false);
    });

    const handleFocus = useEventCallback((event: React.FocusEvent<HTMLSelectElement>): void => {
        onFocus?.(event);
        setFocused?.(true);
    });

    const checkFilled = React.useCallback(
        (value: string | undefined) => {
            setFilled?.(value !== '');
        },
        [setFilled]
    );

    useIsomorphicEffect(() => {
        if (isControlled) {
            checkFilled(valueProp);
        }
    }, [valueProp, checkFilled, isControlled]);

    const handleChange = useEventCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
        if (!isControlled) {
            checkFilled(event.target.value);
        }
        onChange?.(event);
    });

    return (
        <select
            ref={handleRef}
            aria-invalid={error || undefined}
            disabled={disabled || undefined}
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
            tabIndex={tabIndex}
            value={valueProp}
            data-select-base={true}
            {...other}
        >
            {children}
        </select>
    );
});
